<script lang="ts">
  import { onMount } from "svelte"
  import Layout from "$app/Layouts/Embed.svelte"
  import { slide, fade } from "svelte/transition"

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0
  })

  const formatterWithDecimals = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2
  })

  import rowing_img from "@images/home/team_rowing.png"

  interface Props {
    number_of_employees: number
    current_user: UserType
    sent: boolean
  }

  let props = $props()
  let { current_user = null }: Props = props

  let number_of_employees = $state(props.number_of_employees || 50)
  let sent = $state(props.sent || false)

  let error = $state("")

  let results = {
    disengaged: 0,
    disengaged_cost: 0,
    apathetic: 0,
    apathetic_cost: 0,
    engaged: 0,
    engaged_cost: 0,
    total_cost: 0,
    disengaged_improved: 0,
    disengaged_improved_cost: 0,
    engaged_improved: 0,
    cost_impact: 0
  }

  let salary = 85072.0
  let formattedSalary = $state(formatterWithDecimals.format(salary))

  const formatSalary = (e) => {
    if (e.target.value) {
      salary = e.target.value.replace(/\D/g, "")
      formattedSalary = formatterWithDecimals.format(salary)
    }
  }

  let disengagedPercent = $state(16)
  let apatheticPercent = $state(52)
  let engagedPercent = $state(32)

  let disengagedImprovement = 10
  let engagedImprovement = 37

  let errorMessage = $state("")

  function submitForm(): void {
    if (number_of_employees <= 10) {
      errorMessage = "Please enter at least 11 employees for helpful results."
      error = "number_of_employees"
    } else if (salary < 10000) {
      errorMessage = "Please enter a salary of at least $10,000."
      error = "salary"
    } else if (disengagedPercent + apatheticPercent + engagedPercent !== 100) {
      errorMessage = "Please ensure Net Promoter Percentages add up to 100%."
      error = "net_promoter"
    } else {
      results.disengaged = Math.floor(
        number_of_employees * (disengagedPercent / 100)
      )
      results.apathetic = Math.floor(
        number_of_employees * (apatheticPercent / 100)
      )
      results.engaged = Math.floor(number_of_employees * (engagedPercent / 100))

      if (
        results.disengaged + results.apathetic + results.engaged !=
        number_of_employees
      ) {
        results.engaged += 1
      }

      results.disengaged_cost = Math.floor(results.disengaged * salary * 0.34)

      results.apathetic_cost = Math.floor(results.apathetic * salary * 0.18)

      results.total_cost = results.disengaged_cost + results.apathetic_cost

      /* IMPROVED RESULTS */
      results.disengaged_improved = Math.floor(
        number_of_employees * (disengagedImprovement / 100)
      )
      results.engaged_improved = Math.floor(
        number_of_employees * (engagedImprovement / 100)
      )

      if (
        results.disengaged_improved +
          results.apathetic +
          results.engaged_improved !=
        number_of_employees
      ) {
        results.engaged_improved += 1
      }

      results.disengaged_improved_cost = Math.floor(
        results.disengaged_improved * salary * 0.34
      )

      if (
        results.disengaged_improved +
          results.apathetic +
          results.engaged_improved !=
        number_of_employees
      ) {
        results.disengaged_improved += 1
      }

      results.cost_impact =
        results.disengaged_cost - results.disengaged_improved_cost

      error = ""
      errorMessage = ""
      sent = true
    }
  }

  onMount(() => {
    if (sent) {
      submitForm()
    }
  })
</script>

<Layout {...props}>
  <div>
    <div class="relative">
      <div class="relative">
        {#if sent === true}
          <div in:slide out:slide class="relative py-8 sm:py-12 lg:py-16">
            <h1
              class="mt-6 md:mt-8 lg:mt-12 text-center mx-auto text-black text-2xl md:text-3xl font-bold tracking-loose lg:text-4xl"
            >
              Your Company's Return on Engagement (ROE)
            </h1>
            <h2
              class="mt-4 text-2xl md:text-3xl lg:text-4xl font-bold text-center text-amber-800"
            >
              for {number_of_employees} Employees
            </h2>
            <div
              class="relative max-w-4xl mx-8 mt-16 md:mt-20 bg-amber-100 rounded-lg p-6 md:p-8 lg:mx-auto"
            >
              <svg
                class="absolute top-0 left-10 transform -translate-x-8 -translate-y-12 md:-translate-y-20 h-24 w-24 md:h-32 md:w-32 text-amber-800 opacity-50"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 144 144"
                aria-hidden="true"
              >
                <path
                  stroke-width="2"
                  d="M41.485 15C17.753 31.753 1 59.208 1 89.455c0 24.664 14.891 39.09 32.109 39.09 16.287 0 28.386-13.03 28.386-28.387 0-15.356-10.703-26.524-24.663-26.524-2.792 0-6.515.465-7.446.93 2.327-15.821 17.218-34.435 32.11-43.742L41.485 15zm80.04 0c-23.268 16.753-40.02 44.208-40.02 74.455 0 24.664 14.891 39.09 32.109 39.09 15.822 0 28.386-13.03 28.386-28.387 0-15.356-11.168-26.524-25.129-26.524-2.792 0-6.049.465-6.98.93 2.327-15.821 16.753-34.435 31.644-43.742L121.525 15z"
                />
              </svg>

              <blockquote class="relative">
                <div
                  class="mt-4 text-lg md:text-xl leading-9 font-medium text-gray-800"
                >
                  <p>
                    Based upon your number of employees and the August 2024
                    Gallup engagement statistics, if your company were a rowboat
                    this is what it would look like. About 32% are
                    <strong>fully engaged</strong>, 52% are
                    <strong>apathetic</strong>, and 16% are
                    <strong>disengaged</strong> and rowing in the wrong direction.
                    Below is how much it is costing you for not having engaged employees.
                  </p>
                </div>
              </blockquote>
            </div>
            <div class="mt-8 md:mt-12 max-w-4xl grid grid-cols-3 mx-auto px-2">
              <div
                class="flex justify-center text-center font-extrabold text-base sm:text-lg md:text-2xl lg:text-3xl text-red-600"
              >
                {results.disengaged} Disengaged
              </div>
              <div
                class="flex justify-center text-center font-bold text-base sm:text-lg md:text-2xl lg:text-3xl text-yellow-600"
              >
                {results.apathetic} Apathetic
              </div>
              <div
                class="flex justify-center text-center font-bold text-base sm:text-lg md:text-2xl lg:text-3xl text-green-600"
              >
                {results.engaged} Engaged
              </div>
              <div class="col-span-3">
                <img src={rowing_img} class="w-full" alt="Rowing" />
              </div>
            </div>

            <div class="mt-4 md:mt-6 max-w-4xl grid grid-cols-3 mx-auto px-2">
              <div
                class="flex justify-center font-bold text-base md:text-2xl text-red-600"
              >
                -{formatter.format(results.disengaged_cost)}
                <span class="text-xs ml-1 -mt-1">1</span>
              </div>
              <div
                class="flex justify-center font-bold text-base md:text-2xl text-yellow-600"
              >
                -{formatter.format(results.apathetic_cost)}
                <span class="text-xs ml-1 -mt-1">1</span>
              </div>
              <div
                class="flex justify-center font-bold text-2xl text-green-500"
              ></div>
            </div>
            <div class="mt-3 max-w-4xl grid grid-cols-3 mx-auto px-2">
              <div
                class="flex justify-center text-center text-sm md:text-base text-red-700"
              >
                34% Dead Wages <span class="text-xs ml-1 -mt-2">2</span>
              </div>
              <div
                class="flex justify-center text-center text-sm md:text-base text-yellow-700"
              >
                18% Dead Wages <span class="text-xs ml-1 -mt-2">2</span>
              </div>
              <div
                class="flex justify-center text-center text-sm md:text-base text-green-500"
              ></div>
            </div>

            <div
              class="mt-5 md:mt-8 max-w-4xl flex justify-center items-center mx-auto px-2"
            >
              <div class="text-base md:text-xl font-bold text-red-800">
                ANNUAL DEAD WAGES = <span
                  class="text-lg md:text-3xl font-extrabold whitespace-nowrap"
                >
                  -{formatter.format(results.total_cost)}</span
                >
              </div>
            </div>

            <div class="mt-5 md:mt-8 max-w-4xl grid grid-cols-3 mx-auto px-2">
              <div class="col-span-3 pb-8">
                <p
                  class="mt-6 text-base md:text-2xl text-green-800 text-center py-4 px-8 bg-green-100 rounded-sm shadow font-bold"
                >
                  Imagine what your boat could look like with Fast Attack
                  Leadership, increasing overall engagement by 6%.
                </p>
              </div>
              <div
                class="flex justify-center text-center font-extrabold text-base sm:text-lg md:text-2xl lg:text-3xl text-red-600"
              >
                {results.disengaged_improved} Disengaged
              </div>
              <div
                class="flex justify-center text-center font-bold text-base sm:text-lg md:text-2xl lg:text-3xl text-yellow-600"
              >
                {results.apathetic} Apathetic
              </div>
              <div
                class="flex justify-center text-center font-bold text-base sm:text-lg md:text-2xl lg:text-3xl text-green-600"
              >
                {results.engaged_improved} Engaged
              </div>
              <div class="col-span-3">
                <img src={rowing_img} class="w-full" alt="Rowing" />
              </div>
            </div>

            <div
              class="mt-6 md:mt-8 max-w-4xl flex justify-center items-center mx-auto px-2"
            >
              <div class="text-sm md:text-2xl font-bold text-blue-800">
                ANNUAL DEAD WAGES IMPACT = <span
                  class="text-xl md:text-5xl font-extrabold whitespace-nowrap"
                >
                  +{formatter.format(results.cost_impact)}</span
                >
              </div>
            </div>

            <div class="relative">
              <div class="w-full bg-black mt-16 md:mt-24 py-12">
                <div
                  class="relative z-20 text-white text-3xl max-w-4xl mx-8 lg:mx-auto"
                >
                  <div
                    class="absolute -mt-20 inline bg-lwp-purple text-white py-3 px-8 rounded-lg"
                  >
                    THE FAST ATTACK LEADERSHIP SOLUTION
                  </div>
                  <p class="text-lg md:text-2xl lg:text-3xl">
                    On average, companies who utilize Lead With Purpose's one,
                    simple tool to increase employee engagement are 9x more
                    aligned and 6x more productive, saving them
                    <strong>thousands of dollars on every employee</strong>.
                  </p>
                </div>
              </div>

              <div class="mt-12 md:mt-16 z-20 grid grid-cols-1">
                <div
                  class="max-w-4xl mx-auto text-lg md:text-2xl lg:text-3xl text-center bg-amber-100 p-4 font-bold"
                >
                  You can start to increase engagement in your team today
                  through the Lead With Purpose platform.
                </div>
                <div class="flex justify-center mt-8">
                  <a
                    href="/signup"
                    class="flex z-20 items-center text-2xl mx-auto lg:inline justify-center px-8 py-4 border border-transparent font-bold rounded-full shadow text-white bg-green-500 hover:bg-green-600 sm:px-8"
                  >
                    Start Your Free Trial Now!
                  </a>
                </div>
              </div>

              <div class="relative max-w-4xl mx-auto mt-16">
                <p class="text-base md:text-lg lg:text-center px-3">
                  <span class="text-xs mr-1 -mt-1">1</span>
                  The average salary per employee (including fringe benefits) used
                  in the calculation is $85,072 and comes from the
                  <a
                    href="https://www.bls.gov/ncs/ect/"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-700 hover:underline"
                    >Bureau of Labor 2023</a
                  >.
                </p>
                <p class="mt-2 text-base md:text-lg lg:text-center px-3">
                  <span class="text-xs mr-1 -mt-1">2</span>
                  Dead wage figures are based on research statistics compiled by
                  <a
                    href="https://www.forbes.com/sites/karlynborysenko/2019/05/02/how-much-are-your-disengaged-employees-costing-you/?sh=29ebb8693437"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-700 hover:underline">McLean & Company</a
                  >.
                </p>
              </div>

              <div class="flex justify-center mt-6 z-20">
                <button
                  onclick={() => (sent = false)}
                  class="z-20 rounded-full bg-yellow-500 hover:bg-yellow-600 text-white text-xl px-8 py-2"
                >
                  Reset Calculator
                </button>
              </div>
            </div>
          </div>
        {:else}
          <div
            in:slide
            out:slide
            class="mx-auto max-w-lg px-4 sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl"
          >
            <div class="relative px-4 py-6 sm:px-6 sm:py-16 lg:py-20">
              <h1
                class="mt-8 md:mt-12 lg:mt-20 text-center max-w-3xl mx-auto text-black text-2xl font-bold tracking-loose md:text-3xl lg:text-5xl"
              >
                How many of your employees are disengaged, and how much are they
                costing you?
              </h1>
              <div
                class="mt-8 lg:mt-12 mx-auto text-white text-center flex justify-center"
              >
                <p
                  class="text-white text-2xl md:text-3xl bg-lwp-purple font-bold py-2 px-4 rounded-md"
                >
                  Use the Lead With Purpose Engagement Calculator to find out!
                </p>
              </div>
              <div class="mt-8 md:mt-16 mx-auto max-w-2xl">
                <form
                  onsubmit={(e) => {
                    e.preventDefault()
                    submitForm()
                  }}
                  in:fade
                  out:fade
                >
                  {#if errorMessage.length > 0}
                    <div
                      class="bg-red-500 text-center rounded-lg shadow text-white p-3"
                    >
                      {errorMessage}
                    </div>
                  {/if}

                  <div>
                    <label
                      for="number_of_employees"
                      class="block text-2xl md:text-3xl text-center font-bold text-black"
                      >How many employees are at your company?</label
                    >
                    <div
                      class="mt-4 mx-auto w-56 input-cont flex justify-center"
                      class:error={error === "number_of_employees"}
                    >
                      <input
                        type="text"
                        bind:value={number_of_employees}
                        required
                        name="number_of_employees"
                        style="font-size: 1.6rem;"
                        class="py-3 px-4 inline mx-auto text-lg text-center shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  {#if current_user}
                    <div>
                      <label
                        for="number_of_employees"
                        class="block text-2xl md:text-3xl text-center font-bold text-black mt-4"
                        >Average Salary?</label
                      >
                      <div
                        class="mt-4 mx-auto w-56 input-cont flex justify-center"
                        class:error={error === "salary"}
                      >
                        <input
                          type="text"
                          bind:value={formattedSalary}
                          required
                          name="salary"
                          style="font-size: 1.6rem;"
                          onchange={formatSalary}
                          class="py-3 px-4 inline mx-auto text-lg text-center shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                        />
                      </div>
                    </div>

                    <div>
                      <p
                        class="block text-2xl md:text-3xl text-center font-bold text-black mt-6"
                      >
                        Net Promoter Percentages*
                      </p>
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                        <div>
                          <label
                            for="disengaged"
                            class="block text-xl md:text-2xl text-center font-medium text-red-600"
                            >% Disengaged</label
                          >

                          <div
                            class="mt-2 mx-auto w-48 input-cont flex justify-center items-center"
                            class:error={error === "net_promoter"}
                          >
                            <div class="relative mt-1 rounded-md shadow-sm">
                              <input
                                type="number"
                                min="1"
                                max="99"
                                bind:value={disengagedPercent}
                                name="disengaged"
                                style="font-size: 1.5rem;"
                                class="py-3 px-4 inline mx-auto text-lg text-center shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                              />
                              <div
                                class="pointer-events-none absolute inset-y-4 right-0 flex items-center pr-3"
                              >
                                <span
                                  class="mt-1 text-lg text-gray-500 sm:text-base"
                                  >%</span
                                >
                              </div>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div>
                            <label
                              for="apathetic"
                              class="block text-xl md:text-2xl text-center font-medium text-yellow-600"
                              >% Apathetic</label
                            >

                            <div
                              class="mt-2 mx-auto w-48 input-cont flex justify-center items-center"
                              class:error={error === "net_promoter"}
                            >
                              <div class="relative mt-1 rounded-md shadow-sm">
                                <input
                                  type="number"
                                  min="1"
                                  max="99"
                                  bind:value={apatheticPercent}
                                  name="apathetic"
                                  style="font-size: 1.5rem;"
                                  class="py-3 px-4 inline mx-auto text-lg text-center shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                                />
                                <div
                                  class="pointer-events-none absolute inset-y-4 right-0 flex items-center pr-3"
                                >
                                  <span
                                    class="mt-1 text-lg text-gray-500 sm:text-base"
                                    >%</span
                                  >
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div>
                            <label
                              for="engaged"
                              class="block text-xl md:text-2xl text-center font-medium text-green-500"
                              >% Engaged</label
                            >

                            <div
                              class="mt-2 mx-auto w-48 input-cont flex justify-center items-center"
                              class:error={error === "net_promoter"}
                            >
                              <div class="relative mt-1 rounded-md shadow-sm">
                                <input
                                  type="number"
                                  min="1"
                                  max="99"
                                  bind:value={engagedPercent}
                                  name="engaged"
                                  style="font-size: 1.5rem;"
                                  class="py-3 px-4 inline mx-auto text-lg text-center shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                                />
                                <div
                                  class="pointer-events-none absolute inset-y-4 right-0 flex items-center pr-3"
                                >
                                  <span
                                    class="mt-1 text-lg text-gray-500 sm:text-base"
                                    >%</span
                                  >
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p class="text-sm text-center mt-4">
                        * Default percentages from Aug 2024 Gallup Statistics
                      </p>
                    </div>
                  {/if}
                  <div class="flex justify-center mt-6">
                    <button
                      type="submit"
                      class="rounded-full bg-green-500 hover:bg-green-600 text-white text-2xl px-12 py-3 font-bold tracking-wide"
                    >
                      Calculate!
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>
</Layout>
